import type { CustomDisciplineLabelTranslation } from "./types.js";
import type { LayoutDocument } from "../layout/types.js";
import {
  getCustomDisciplineFieldTranslationKey,
  getCustomDisciplineOptionTranslationKey,
  getCustomDisciplineActionTranslationKey,
  getCustomDisciplineActionTooltipTranslationKey,
  getCustomDisciplineModalTranslationKey,
  getCustomDisciplineGroupTypeNameTranslationKey,
  getCustomDisciplineNameTranslationKey,
} from "./label-keys.js";

/** Same source-label convention as SELECT options, scoped to one discipline. */
export const getCustomDisciplineTextTranslationKey = (
  code: string,
  text: string,
) => {
  // Two independent 32-bit hashes keep even long instructions within the indexed DB key limit.
  let a = 2166136261,
    b = 5381;
  for (let i = 0; i < text.length; i++) {
    a = Math.imul(a ^ text.charCodeAt(i), 16777619);
    b = Math.imul(b, 33) ^ text.charCodeAt(i);
  }
  return `customDiscipline.group.${code}.texts.${(a >>> 0).toString(16).padStart(8, "0")}${(b >>> 0).toString(16).padStart(8, "0")}`;
};

/** Optional literal fragments for labels assembled with data by a Display Editor graph. */
export const getCustomDisciplineFragmentTranslationKey = (
  code: string,
  text: string,
) => `customDiscipline.group.${code}.fragments.${encodeURIComponent(text)}`;

/** Only presentation properties are exported; graph identifiers and stored values are excluded. */
export function collectCustomDisciplinePresentationLabels(
  value: unknown,
  labels = new Set<string>(),
): Set<string> {
  if (typeof value === "string" && /^[\[{]/.test(value.trim())) {
    try {
      return collectCustomDisciplinePresentationLabels(
        JSON.parse(value),
        labels,
      );
    } catch {
      return labels;
    }
  }
  if (!value || typeof value !== "object") return labels;
  for (const [key, child] of Object.entries(value)) {
    if (
      [
        "behavior",
        "referenceFilterBehavior",
        "graph",
        "ports",
        "nodes",
        "edges",
        "catalog",
      ].includes(key)
    )
      continue;
    if (
      typeof child === "string" &&
      ["text", "label", "headerGroup"].includes(key) &&
      child.trim()
    )
      labels.add(child);
    else if (typeof child === "object" || /layout/i.test(key))
      collectCustomDisciplinePresentationLabels(child, labels);
  }
  return labels;
}

const dictionaries = new WeakMap<
  object,
  WeakMap<object, Map<string, Record<string, string>>>
>();
const emptyLabels: Record<string, string> = Object.freeze({});

/** A display-only dictionary. Never use it to build Behavior records or persist input values. */
type LabelGroupType = {
  code: string;
  name: string;
  fields: readonly {
    key: string;
    label: string;
    scope: string;
    type: string;
    options: readonly { label: string }[];
  }[];
  actions: readonly {
    key: string;
    label: string;
    iconTooltip?: string | null;
  }[];
  modals?: readonly { key: string; label: string }[];
  discipline?: { code: string; name: string } | null;
};
export function customDisciplineLabels(
  groupType: LabelGroupType | null | undefined,
  translations: readonly Pick<
    CustomDisciplineLabelTranslation,
    "locale" | "key" | "label"
  >[],
  locale: string,
): Record<string, string> {
  if (!groupType) return emptyLabels;
  let byTranslations = dictionaries.get(groupType);
  if (!byTranslations)
    dictionaries.set(groupType, (byTranslations = new WeakMap()));
  let byLocale = byTranslations.get(translations);
  if (!byLocale) byTranslations.set(translations, (byLocale = new Map()));
  const cached = byLocale.get(locale);
  if (cached) return cached;
  const lang = locale.toLowerCase().split("-")[0];
  const entries = new Map<string, string>();
  // Exact locale wins regardless of database ordering.
  for (const t of translations)
    if (t.locale.toLowerCase().split("-")[0] === lang)
      entries.set(t.key, t.label);
  for (const t of translations)
    if (t.locale.toLowerCase() === locale.toLowerCase())
      entries.set(t.key, t.label);
  const result: Record<string, string> = Object.create(null);
  const add = (key: string, source: string) => {
    const label = entries.get(key);
    if (label) result[source] = label;
  };
  for (const f of groupType.fields) {
    add(getCustomDisciplineFieldTranslationKey(groupType.code, f), f.label);
    for (const o of f.options)
      add(
        getCustomDisciplineOptionTranslationKey(groupType.code, f, o),
        o.label,
      );
    if (f.type === "FOREIGN_KEY") {
      const prefix =
        getCustomDisciplineFieldTranslationKey(groupType.code, f) + ".";
      for (const [key, label] of entries)
        if (key.startsWith(prefix)) result[key.slice(prefix.length)] = label;
    }
  }
  for (const a of groupType.actions) {
    add(getCustomDisciplineActionTranslationKey(groupType.code, a), a.label);
    if (a.iconTooltip)
      add(
        getCustomDisciplineActionTooltipTranslationKey(groupType.code, a),
        a.iconTooltip,
      );
  }
  for (const m of groupType.modals ?? [])
    add(getCustomDisciplineModalTranslationKey(groupType.code, m), m.label);
  add(
    getCustomDisciplineGroupTypeNameTranslationKey(groupType.code),
    groupType.name,
  );
  if (groupType.discipline)
    add(
      getCustomDisciplineNameTranslationKey(groupType.discipline.code),
      groupType.discipline.name,
    );
  const prefix = `customDiscipline.group.${groupType.code}.texts.`;
  for (const [key, label] of entries)
    if (key.startsWith(prefix))
      result[`@text:${key.slice(prefix.length)}`] = label;
  const fragmentPrefix = `customDiscipline.group.${groupType.code}.fragments.`;
  for (const [key, label] of entries)
    if (key.startsWith(fragmentPrefix)) {
      try {
        const source = decodeURIComponent(key.slice(fragmentPrefix.length));
        result[`@fragment:${source}`] =
          `${source.match(/^\s*/)?.[0] ?? ""}${label.trim()}${source.match(/\s*$/)?.[0] ?? ""}`;
      } catch {
        /* Ignore malformed source fragments. */
      }
    }
  Object.freeze(result);
  byLocale.set(locale, result);
  return result;
}

/** An explicit dictionary lookup, with a French/source fallback. */
const fragments = new WeakMap<
  object,
  { pattern: RegExp; values: Map<string, string> } | null
>();
export function customDisciplineLabel(
  labels: Record<string, string> | undefined,
  source: string,
): string {
  if (!labels) return source;
  if (Object.prototype.hasOwnProperty.call(labels, source))
    return labels[source]!;
  const hash = getCustomDisciplineTextTranslationKey("", source).split(
    ".texts.",
  )[1];
  const exact = labels[`@text:${hash}`];
  if (exact != null) return exact;
  let fragment = fragments.get(labels);
  if (fragment === undefined) {
    const pairs = Object.entries(labels)
      .filter(([key]) => key.startsWith("@fragment:"))
      .map(([key, value]) => [key.slice(10), value] as const)
      .filter(([key]) => key.length > 0)
      .sort((a, b) => b[0].length - a[0].length);
    fragment = pairs.length
      ? {
          pattern: new RegExp(
            pairs
              .map(([key]) => key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
              .join("|"),
            "g",
          ),
          values: new Map(pairs),
        }
      : null;
    fragments.set(labels, fragment);
  }
  return fragment
    ? source.replace(fragment.pattern, (match) => fragment!.values.get(match)!)
    : source;
}

/** Translate immutable documents once per dictionary, without touching Behavior graphs or values. */
const localizedDocuments = new WeakMap<
  LayoutDocument,
  WeakMap<object, LayoutDocument>
>();
export function localizeCustomDisciplineDocument(
  document: LayoutDocument,
  labels?: Record<string, string>,
): LayoutDocument {
  if (!labels || !Object.keys(labels).length) return document;
  let cache = localizedDocuments.get(document);
  if (!cache) localizedDocuments.set(document, (cache = new WeakMap()));
  const cached = cache.get(labels);
  if (cached) return cached;
  const visit = (value: unknown): unknown => {
    if (!value || typeof value !== "object") return value;
    if (Array.isArray(value)) {
      const next = value.map(visit);
      return next.some((v, i) => v !== value[i]) ? next : value;
    }
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      const translated = [
        "behavior",
        "graph",
        "catalog",
        "staticOptions",
      ].includes(key)
        ? child
        : typeof child === "string" && ["text", "label"].includes(key)
          ? customDisciplineLabel(labels, child)
          : typeof child === "object"
            ? visit(child)
            : child;
      next[key] = translated;
      changed ||= translated !== child;
    }
    return changed ? next : value;
  };
  const result = visit(document) as LayoutDocument;
  cache.set(labels, result);
  return result;
}
